import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X, MapPin, Clock, ArrowLeft, Loader2 } from "lucide-react";

export default function PackageDetails() {
  const { id } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getPackage(id).then(setPkg).catch(() => setPkg(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return (<div className="py-20 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>);
  if (!pkg) return (<div className="py-20 text-center text-slate-600">Package not found. <Link className="text-[#1D4ED8] underline" to="/domestic">Browse packages</Link></div>);

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/domestic" className="inline-flex items-center gap-1 text-sm text-[#1D4ED8] hover:underline mb-6"><ArrowLeft className="w-4 h-4" /> Back</Link>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <img src={pkg.image_url} className="w-full h-[420px] object-cover rounded-3xl shadow-xl" alt={pkg.title} />
            <div className="mt-6">
              <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                {pkg.destination && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{pkg.destination}</span>}
                {pkg.duration && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{pkg.duration}</span>}
                <Badge variant="secondary">{pkg.type}</Badge>
              </div>
              <h1 className="text-3xl lg:text-4xl font-display font-bold text-[#0B1B3A]">{pkg.title}</h1>
              <p className="mt-3 text-slate-700 whitespace-pre-line leading-relaxed">{pkg.description}</p>

              {pkg.highlights?.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-display font-semibold text-lg text-[#0B1B3A] mb-2">Highlights</h3>
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {pkg.highlights.map((h, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-green-600 mt-0.5" /> {h}</li>)}
                  </ul>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                {pkg.inclusions?.length > 0 && (
                  <Card className="p-5 bg-white/70 backdrop-blur-xl border-white/40 rounded-2xl">
                    <h4 className="font-display font-semibold text-[#0B1B3A] mb-2">Inclusions</h4>
                    <ul className="space-y-1.5">{pkg.inclusions.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-3.5 h-3.5 text-green-600 mt-0.5" /> {x}</li>)}</ul>
                  </Card>
                )}
                {pkg.exclusions?.length > 0 && (
                  <Card className="p-5 bg-white/70 backdrop-blur-xl border-white/40 rounded-2xl">
                    <h4 className="font-display font-semibold text-[#0B1B3A] mb-2">Exclusions</h4>
                    <ul className="space-y-1.5">{pkg.exclusions.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><X className="w-3.5 h-3.5 text-red-500 mt-0.5" /> {x}</li>)}</ul>
                  </Card>
                )}
              </div>
            </div>
          </div>

          <div>
            <Card className="sticky top-24 p-6 rounded-2xl border-white/40 bg-white/80 backdrop-blur-xl">
              {pkg.price ? (
                <>
                  <div className="text-xs text-slate-500">Starting from</div>
                  <div className="font-display font-bold text-3xl text-[#1D4ED8]">₹{Number(pkg.price).toLocaleString("en-IN")}</div>
                  <div className="text-xs text-slate-500 mt-1">{pkg.price_label}</div>
                </>
              ) : <div className="text-lg font-semibold text-[#1D4ED8]">Price on Request</div>}
              <Link to="/contact"><Button className="w-full mt-5 rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF]" data-testid="package-inquire-button">Inquire Now</Button></Link>
              <a href="tel:+919753277771"><Button variant="outline" className="w-full mt-2 rounded-xl border-blue-200 bg-white/60">Call Lucky</Button></a>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
